import React, { useState } from "react";
import Select, { MultiValue } from "react-select";
import { Modal } from "../../common/Modal/Modal";
import { styled } from "../../../stitches.config";
import { Box, FlexBox, Option } from "../../primitives";
import {
  LabelStyled,
  PrimaryButton,
  SecondaryButton,
  StyledInput,
  StyledSelect,
  TitleStyle,
} from "../../common/common.style";
import {
  CARDMARKET_SELLER_ACOUNTRY_CODE,
  CardMarketSellerType,
  CardMarketStrategyEnum,
} from "magic-shared-types";
import { InfoTooltip } from "../../common/InfoTooltip/InfoTooltip";
import { CheckBox } from "../../common/CheckBox/CheckBox";
import { Group, LabelGroup, Section } from "../modalCreateStrategy.styled";
import { LoadingOverlay } from "../../common/LoadingOverley/LoadingOverley";
import toast from "react-hot-toast";
import { useCreateCardMarketPriceStrategyMutation } from "../../../services/myServer/priceStrategy";

type OptionType = {
  value: string;
  label: string;
};

const Wrapper = styled(Box, {
  padding: 20,
});

interface ModalCreateStrategyCardMarketProps {
  setIsOpenModalCreateStrategyPrice: (arg1: boolean) => void;
  refetch: () => void;
}

export const ModalCreateStrategyCardMarket = ({
  setIsOpenModalCreateStrategyPrice,
  refetch,
}: ModalCreateStrategyCardMarketProps) => {
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<OptionType[]>([]);
  const [sellerType, setSellerType] = useState<CardMarketSellerType>(
    CardMarketSellerType.ALL
  );
  const [minValue, setMinValue] = useState(0.2);
  const [minFeedback, setMinFeedback] = useState(0);
  const [isSameLanguage, setIsSameLanguage] = useState(false);
  const [isExSameAsNm, setIsExSameAsNm] = useState(false);
  const [strategyName, setStrategyName] = useState("");
  const [excludeSellers, setExcludeSellers] = useState("");
  const [strategy, setStrategy] = useState<CardMarketStrategyEnum>(
    CardMarketStrategyEnum.FIRST_PRICE
  );
  const mappedLocations = CARDMARKET_SELLER_ACOUNTRY_CODE.map((country) => ({
    value: country.name,
    label: country.name,
  }));
  const [createCardMarketPriceStrategy] =
    useCreateCardMarketPriceStrategyMutation();

  const handleChange = (value: MultiValue<OptionType>) => {
    setSelected([...value] as OptionType[]);
  };

  const onClickCreate = async () => {
    setLoading(true);
    try {
      await createCardMarketPriceStrategy({
        name: strategyName,
        location: selected.map((opt) => opt.value).join(","),
        ex_same_as_nm: isExSameAsNm,
        feedback: minFeedback,
        min_price: minValue,
        same_language: isSameLanguage,
        exclude_sellers: excludeSellers,
        strategy,
        seller_type: sellerType,
      });
      setIsOpenModalCreateStrategyPrice(false);
      toast.success("Strategy saved succesfully");
      refetch();
    } catch (e) {
      toast.error("Error trying to save strategy");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <LoadingOverlay />}
      <Modal onClose={() => setIsOpenModalCreateStrategyPrice(false)}>
        <Wrapper css={{ height: "450px" }}>
          <TitleStyle>Create a new cardmarket strategy</TitleStyle>
          <Section>
            <Group>
              <LabelGroup>
                <LabelStyled>Strategy Name</LabelStyled>
                <InfoTooltip content="Your strategy name" />
              </LabelGroup>
              <StyledInput
                type="text"
                value={strategyName}
                onChange={(e) => setStrategyName(e.target.value)}
              />
            </Group>
            <Group>
              <LabelGroup>
                <LabelStyled>Seller Location</LabelStyled>
                <InfoTooltip content="Select one or multiple countries where the seller is located. This helps narrow down price comparisons to specific geographic regions." />
              </LabelGroup>
              <Select
                options={mappedLocations}
                isMulti
                value={selected}
                onChange={handleChange}
                placeholder="Choose seller location..."
                styles={{
                  container: (base) => ({
                    ...base,
                    width: 300,
                  }),
                }}
              />
            </Group>

            <Group>
              <LabelGroup>
                <LabelStyled>Seller Type</LabelStyled>
                <InfoTooltip content="Choose the type of seller to consider: ALL, PROFESSIONAL, or POWERSELLER." />
              </LabelGroup>
              <StyledSelect
                value={sellerType}
                onChange={(e) =>
                  setSellerType(e.target.value as CardMarketSellerType)
                }
              >
                <Option value={CardMarketSellerType.ALL}>
                  {CardMarketSellerType.ALL}
                </Option>
                <Option value={CardMarketSellerType.PROFESSIONAL}>
                  {CardMarketSellerType.PROFESSIONAL}
                </Option>
                <Option value={CardMarketSellerType.POWERSELLER}>
                  {CardMarketSellerType.POWERSELLER}
                </Option>
              </StyledSelect>
            </Group>
          </Section>

          <Section>
            <Group>
              <LabelGroup>
                <LabelStyled>Strategy</LabelStyled>
                <InfoTooltip content="Select the pricing strategy: FIRST_PRICE (lowest price) or AVERAGE (average price from sellers)." />
              </LabelGroup>
              <StyledSelect
                value={strategy}
                onChange={(e) =>
                  setStrategy(e.target.value as CardMarketStrategyEnum)
                }
              >
                <Option value={CardMarketStrategyEnum.FIRST_PRICE}>
                  {CardMarketStrategyEnum.FIRST_PRICE}
                </Option>
                <Option value={CardMarketStrategyEnum.AVARAGE}>
                  {CardMarketStrategyEnum.AVARAGE}
                </Option>
              </StyledSelect>
            </Group>
            <Group>
              <LabelGroup>
                <LabelStyled>Minimum Price</LabelStyled>
                <InfoTooltip content="Set the minimum price threshold for items to be included, preventing very low offers from skewing data." />
              </LabelGroup>
              <StyledInput
                type="number"
                value={minValue}
                onChange={(e) => setMinValue(+e.target.value)}
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Group>

            <Group>
              <LabelGroup>
                <LabelStyled>Minimum Feedback</LabelStyled>
                <InfoTooltip content="Specify the minimum seller feedback score to ensure reliable sellers are considered." />
              </LabelGroup>
              <StyledInput
                type="number"
                value={minFeedback}
                onChange={(e) => setMinFeedback(+e.target.value)}
                onKeyDown={(e) => {
                  if (["e", "E", "+", "-"].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
              />
            </Group>

            <Group>
              <LabelGroup>
                <LabelStyled>
                  Exclude sellers name (separate them by commas)
                </LabelStyled>
                <InfoTooltip content="Enter seller usernames to exclude, separated by commas (e.g., seller1,seller2). No spaces." />
              </LabelGroup>
              <StyledInput
                type="text"
                placeholder="seller1,seller2,seller3......"
                value={excludeSellers}
                onChange={(e) => setExcludeSellers(e.target.value)}
              />
            </Group>
          </Section>

          <Section>
            <FlexBox css={{ alignItems: "center", gap: 10 }}>
              <LabelStyled>Only same language</LabelStyled>
              <InfoTooltip content="Limit comparisons to sellers offering cards in the same language as your target card." />
              <CheckBox
                checked={isSameLanguage}
                setChecked={(checked: boolean) => {
                  setIsSameLanguage(checked);
                }}
              />
            </FlexBox>

            <FlexBox css={{ alignItems: "center", gap: 10 }}>
              <LabelStyled>NM same as EX</LabelStyled>
              <InfoTooltip content="Treat Near Mint (NM) condition the same as Excellent (EX) in price comparison." />
              <CheckBox
                checked={isExSameAsNm}
                setChecked={(checked: boolean) => {
                  setIsExSameAsNm(checked);
                }}
              />
            </FlexBox>
          </Section>
          <FlexBox css={{ justifyContent: "center", marginTop: 40, gap: 20 }}>
            <PrimaryButton isBig css={{ width: 140 }} onClick={onClickCreate}>
              CREATE STRATEGY
            </PrimaryButton>
            <SecondaryButton
              isBig
              css={{ width: 140 }}
              onClick={() => setIsOpenModalCreateStrategyPrice(false)}
            >
              CLOSE
            </SecondaryButton>
          </FlexBox>
        </Wrapper>
      </Modal>
    </>
  );
};
